/*
 * <dwm-query-typer> — the search bar in the SEO & GEO hero (sections/dwm-query-hero.liquid).
 *
 * The server renders the first example query, so without JavaScript the bar simply shows it. When motion is allowed
 * the queries in `data-queries` ("a|b|c") rotate calmly and keep cycling: type → hold → erase → next, and after the
 * last one back to the first. With prefers-reduced-motion (also when it changes while the page is open) it stops and
 * shows the first query again.
 *
 * Calm and cheap: one timer at a time; paused while the bar is out of view or the tab is hidden, resumed where it
 * left off. The text changes inside a fixed-width bar (ellipsis), so nothing shifts. Visual only: the whole diagram is
 * aria-hidden, so screen readers never hear the letters change.
 */
import { isMotionAllowed } from 'dwm-motion';

const TYPE_MS = 45;
const ERASE_MS = 30;
const HOLD_MS = 1500;
const GAP_MS = 250;

class DwmQueryTyper extends HTMLElement {
  #queries = [];
  #target = null;
  #index = 0;
  #chars = 0;
  #phase = 'hold';
  #timer = 0;
  #visible = false;
  #observer = null;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#queries = (this.dataset.queries || '').split('|').map((query) => query.trim()).filter(Boolean);
    this.#target = this.querySelector('[data-query]');
    if (!this.#target || this.#queries.length < 2) return;

    this.#chars = this.#queries[0].length;
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    });
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#sync);
    this.#motionQuery.addEventListener('change', this.#onMotionChange);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#sync);
    this.#motionQuery.removeEventListener('change', this.#onMotionChange);
  }

  get #running() {
    return this.#visible && !document.hidden && isMotionAllowed();
  }

  #sync = () => {
    if (this.#running) {
      if (!this.#timer) this.#schedule(this.#phase === 'hold' ? HOLD_MS : TYPE_MS);
    } else {
      this.#stop();
    }
  };

  #onMotionChange = () => {
    if (!isMotionAllowed()) {
      // Reduced motion: back to the first query, static.
      this.#stop();
      this.#index = 0;
      this.#chars = this.#queries[0].length;
      this.#phase = 'hold';
      this.#render();
    }
    this.#sync();
  };

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = 0;
    this.classList.remove('is-typing');
  }

  #schedule(ms) {
    this.#timer = setTimeout(() => {
      this.#timer = 0;
      if (this.#running) this.#step();
    }, ms);
  }

  #step() {
    if (this.#phase === 'hold') {
      this.#phase = 'erase';
    }

    if (this.#phase === 'erase') {
      this.classList.add('is-typing');
      this.#chars -= 1;
      this.#render();
      if (this.#chars > 0) return this.#schedule(ERASE_MS);
      this.#index = (this.#index + 1) % this.#queries.length;
      this.#phase = 'type';
      return this.#schedule(GAP_MS);
    }

    // type
    this.#chars += 1;
    this.#render();
    if (this.#chars < this.#queries[this.#index].length) return this.#schedule(TYPE_MS);
    this.#phase = 'hold';
    this.classList.remove('is-typing');
    this.#schedule(HOLD_MS);
  }

  #render() {
    this.#target.textContent = this.#queries[this.#index].slice(0, Math.max(0, this.#chars));
  }
}

if (!customElements.get('dwm-query-typer')) customElements.define('dwm-query-typer', DwmQueryTyper);
